import { FC, ReactNode } from 'react';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

/**
 * Root Store using Zustand
 * 
 * This file sets up the root store that connects all context-specific stores
 * and provides a central access point for the application state.
 */

// Root store state interface
interface RootStoreState {
  // Store references
  adminStore: any | null;
  proctorStore: any | null;
  testTakerStore: any | null;
  facultyStore: any | null;
  publicStore: any | null;
  authStore: any | null;
  uiStore: any | null;
  
  // Actions
  setAdminStore: (store: any) => void;
  setProctorStore: (store: any) => void;
  setTestTakerStore: (store: any) => void;
  setFacultyStore: (store: any) => void;
  setPublicStore: (store: any) => void;
  setAuthStore: (store: any) => void;
  setUIStore: (store: any) => void;
}

/**
 * Root Store
 * 
 * This is the main store that connects all other stores.
 * It provides references to all other stores and methods to set them.
 */
export const useRootStore = create<RootStoreState>()(devtools((set) => ({
  // Initial state
  adminStore: null,
  proctorStore: null,
  testTakerStore: null,
  facultyStore: null,
  publicStore: null,
  authStore: null,
  uiStore: null,
  
  // Actions
  setAdminStore: (store) => set({ adminStore: store }),
  setProctorStore: (store) => set({ proctorStore: store }),
  setTestTakerStore: (store) => set({ testTakerStore: store }),
  setFacultyStore: (store) => set({ facultyStore: store }),
  setPublicStore: (store) => set({ publicStore: store }),
  setAuthStore: (store) => set({ authStore: store }),
  setUIStore: (store) => set({ uiStore: store }),
}), { name: 'root-store' }));

// Root store provider props
type RootStoreProviderProps = {
  children: ReactNode;
};

/**
 * Root Store Provider
 * 
 * Provides the root store to the entire application.
 * All context-specific stores can access the root store through this provider.
 */
export const RootStoreProvider: FC<RootStoreProviderProps> = ({ children }) => {
  // With Zustand, we don't need a context provider since the store is globally accessible
  // Just render the children directly
  return <>{children}</>;
};

// Export the store directly
export type { RootStoreState };

